import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react';
import { ChevronDown, Pencil, Pin, PinOff, SmilePlus, Trash } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@avanta/shared/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@avanta/shared/components/ui/DropdownMenu';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import {
  useActiveOtherUserId,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { cn } from '@avanta/shared/utils/cn';
import { DeleteMessage } from '../DeleteMessage';
import { EMOJI_CATEGORIES } from './emoji-categories';
import { type ActionsProps } from './types';

export const Actions = ({
  message,
  notShowEdit,
  notShow,
  onEdit,
}: ActionsProps): React.ReactElement => {
  const otherUserId = useActiveOtherUserId();
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const pinnedMessage = useMessagesStore(state =>
    activeChannelId ? (state.pinnedMessages.get(activeChannelId) ?? null) : null
  );
  const { pinMessage, unpinMessage, toggleReaction } = useMessagesStore();
  const isPinned = pinnedMessage?.timetoken === message.timetoken;
  const notMine = message.userId === otherUserId;
  const [showReactionsSelector, setShowReactionsSelector] = useState(false);
  const [open, setOpen] = useState(false);
  const { showDialog } = useDialogContext();
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const emojiCategories = useMemo(
    () =>
      EMOJI_CATEGORIES.map(({ category, nameKey }) => ({
        category,
        name: t(nameKey),
      })),
    [t]
  );

  const handleOnPin = async (): Promise<void> => {
    if (!activeChannelId) return;
    if (isPinned) {
      await unpinMessage(activeChannelId);
    } else {
      await pinMessage(message);
    }
  };

  const handleOnReaction = async (reaction: EmojiClickData): Promise<void> => {
    await toggleReaction(message, reaction.emoji);
    setShowReactionsSelector(false);
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        {showReactionsSelector && (
          <Dialog
            onClose={() => setShowReactionsSelector(false)}
            className="w-full bg-transparent border-none shadow-none p-0"
            open
            hideClose
          >
            <EmojiPicker
              previewConfig={{ showPreview: false }}
              categories={emojiCategories}
              onEmojiClick={handleOnReaction}
              searchPlaceHolder="Buscar"
              width="100%"
              className="p-2 border-none"
            />
          </Dialog>
        )}
        <DropdownMenuTrigger
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-2xl border border-teal-300 bg-white p-2.5',
            notShow && !open && 'pointer-events-none opacity-0'
          )}
        >
          <ChevronDown className="size-4 text-denim-800" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align={notMine ? 'end' : 'start'}>
          {notShowEdit && !notMine && (
            <DropdownMenuItem className="hover:bg-gray-100" onClick={onEdit}>
              <Pencil /> {t('Chat.ContextualMenu.edit')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="hover:bg-gray-100" onClick={handleOnPin}>
            {isPinned ? (
              <>
                <PinOff /> {t('Chat.ContextualMenu.unpin')}
              </>
            ) : (
              <>
                <Pin /> {t('Chat.ContextualMenu.pin')}
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="hover:bg-gray-100"
            onClick={() => setShowReactionsSelector(prev => !prev)}
          >
            <SmilePlus />
            {t('Chat.ContextualMenu.react')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              showDialog({
                component: <DeleteMessage message={message} />,
              })
            }
            className={cn('hover:bg-gray-100', notMine && 'hidden')}
          >
            <Trash /> {t('Chat.ContextualMenu.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
